import React, { useRef, useEffect, useState } from 'react';
import { Spin, Card, Button, Input, Row, Col, Select } from 'antd';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import DraggableResizableField from './draggable-resizable-field';
import { rabbitSignFieldTypes } from './../../../../../../../shared/constants/enums';

const { Option } = Select;

const PdfViewer = ({
  data,
  rootContainer,
  loading,
  fields,
  setFields,
  currentPageNumber,
  setCurrentPageNumber,
  currentFileIndex,
  setPageHeights,
  openNewFieldSettings,
  openEditFieldSettings,
  emails,
}) => {
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfDocument, setPDFDocument] = useState();
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);

  const canvasRef = useRef(null);

  useEffect(() => {
    if (data) {
      setPdfLoading(true);
      renderPdf(data);
    }
  }, [data]);

  useEffect(() => {
    if (pdfDocument) {
      setPdfLoading(true);
      renderPage(pdfDocument);
    }
  }, [currentPageNumber, scale]);

  const renderPdf = (fileBytes) => {
    if (canvasRef?.current) {
      pdfjsLib.getDocument(fileBytes).promise.then((pdfDocument) => {
        setTotalPages(pdfDocument.numPages);

        const heightsPromises = [];
        for (let i = 1; i <= pdfDocument.numPages; i++) {
          heightsPromises.push(pdfDocument.getPage(i).then((page) => page.getViewport({ scale }).height));
        }

        Promise.all(heightsPromises).then((heights) => {
          setPageHeights(heights);
          renderPage(pdfDocument);
          setPDFDocument(pdfDocument);
        });
      });
    }
  };

  const renderPage = (localPdfDocument) => {
    localPdfDocument.getPage(currentPageNumber).then((page) => {
      const viewport = page.getViewport({ scale: scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      page.render(renderContext).promise.then(() => {
        if (canvasRef?.current) {
          while (canvasRef?.current?.firstChild) {
            canvasRef.current.removeChild(canvasRef.current.firstChild);
          }
          canvasRef.current.appendChild(canvas);

          setTimeout(() => {
            setPdfLoading(false);
          }, 300);

          canvasRef.current.style.width = `${canvas.width}px`;
          canvasRef.current.style.height = `${canvas.height}px`;
        }
      });
    });
  };

  const goToPrevPage = () => {
    setCurrentPageNumber((prevPage) => Math.max(prevPage - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPageNumber((prevPage) => Math.min(prevPage + 1, totalPages));
  };

  const handlePageInputChange = (e) => {
    const pageNumber = Number(e.target.value);
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPageNumber(pageNumber);
    }
  };

  const handleScaleChange = (value) => {
    setScale(value);
  };

  const increaseScale = () => {
    setScale((prevScale) => Math.min(prevScale + 0.25, 3.0));
  };

  const decreaseScale = () => {
    setScale((prevScale) => Math.max(prevScale - 0.25, 0.5));
  };

  const handleDragEnd = (drawId, x, y, width, height, page0Y) => {
    setFields((prevFields) => prevFields.map((field) => (field.drawId === drawId ? { ...field, x, y, width, height, page0Y } : field)));
  };

  const handleDelete = (drawId) => {
    setFields((prevFields) => prevFields.filter((field) => field.drawId !== drawId));
  };

  const handleContextMenu = (event) => {
    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();

    const pdfDocumentContainer = event.currentTarget;
    const rect = pdfDocumentContainer.getBoundingClientRect();

    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;

    openNewFieldSettings({
      x: (relativeX / scale).toFixed(),
      y: (relativeY / scale).toFixed(),
    });
  };

  return (
    <Spin spinning={loading || pdfLoading}>
      <div className="rabbit-sign-pdf-viewer">
        <Card title={null} bodyStyle={{ padding: 0, display: 'flex', justifyContent: 'center', alignContent: 'center' }} className="pdf-viewer-card">
          <div id="pdfDocument" className="pdf-document">
            <div id="pdfDocumentContainer" className="pdf-document-container" ref={canvasRef} onContextMenu={handleContextMenu}>
              {canvasRef?.current && fields && !pdfLoading
                ? fields
                    .filter((field) => field.docNumber === currentFileIndex && field.pageIndex === currentPageNumber - 1)
                    .map((field) => {
                      const fieldSetting = Object.values(rabbitSignFieldTypes).find((x) => x.value === field.fieldType);
                      const signer = emails.find((x) => x.email === field.email);

                      return (
                        <DraggableResizableField
                          key={field.drawId}
                          drawId={field.drawId}
                          x={field.x}
                          y={field.y}
                          width={field.width}
                          height={field.height}
                          scale={scale}
                          onDragEnd={handleDragEnd}
                          onDelete={handleDelete}
                          email={field.email}
                          page0YPrevPages={field.page0YPrevPages}
                          openEditFieldSettings={openEditFieldSettings}
                          fieldType={fieldSetting.text}
                          fieldColor={fieldSetting.color}
                          signerColor={signer.signerColor}
                          signerLabel={signer.signerLabel}
                        />
                      );
                    })
                : null}
            </div>
          </div>
        </Card>

        <Row justify="center" align="middle" className="pdf-control-button-row">
          <Col>
            <Button onClick={goToPrevPage} disabled={currentPageNumber === 1 || pdfLoading || !canvasRef?.current}>
              Previous
            </Button>
          </Col>
          <Col className="control-button-col">
            <Input
              type="number"
              min={1}
              max={totalPages}
              value={currentPageNumber}
              disabled={pdfLoading}
              onChange={handlePageInputChange}
              className="control-button-input"
            />
            / {totalPages}
          </Col>
          <Col className="control-button-col">
            <Button onClick={goToNextPage} disabled={currentPageNumber === totalPages || pdfLoading || !canvasRef?.current}>
              Next
            </Button>
          </Col>
          <Col>
            <Button onClick={decreaseScale} disabled={scale <= 0.5 || pdfLoading || !canvasRef?.current}>
              -
            </Button>
          </Col>
          <Col>
            <Select
              value={scale}
              onChange={handleScaleChange}
              disabled={pdfLoading || !canvasRef?.current}
              className="control-button-scale"
              getPopupContainer={rootContainer}
            >
              <Option value={0.5}>50%</Option>
              <Option value={0.75}>75%</Option>
              <Option value={1.0}>100%</Option>
              <Option value={1.25}>125%</Option>
              <Option value={1.5}>150%</Option>
              <Option value={1.75}>175%</Option>
              <Option value={2.0}>200%</Option>
              <Option value={2.25}>225%</Option>
              <Option value={2.5}>250%</Option>
              <Option value={2.75}>275%</Option>
              <Option value={3.0}>300%</Option>
            </Select>
          </Col>
          <Col>
            <Button onClick={increaseScale} disabled={scale >= 3.0 || pdfLoading || !canvasRef?.current}>
              +
            </Button>
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default PdfViewer;
